// Macro Causal Graph — Data Definitions

export interface MacroRow {
  id: string;
  title: string;
}

export interface MacroNode {
  id: string;
  row: string;
  subgroup: string;
  icon: string;
  label: string;
  desc: string;
}

export interface MacroEdge {
  id: string;
  from: string;
  to: string;
  sign: number;
  w: number;
  label: string;
  why: string;
}

export interface MacroScenario {
  id: string;
  tag: string;
  name: string;
  shock: string;
  summary: string;
  paths: string[][];
}

export const ROWS: MacroRow[] = [
  { id: 'Events', title: 'TRIGGERS / EVENTS' },
  { id: 'GlobalMacro', title: 'GLOBAL MACRO' },
  { id: 'RussiaMacro', title: 'RUSSIA MACRO' },
  { id: 'Policy', title: 'POLICY' },
  { id: 'RatesCurve', title: 'RATES & CURVE' },
  { id: 'RiskLiquidity', title: 'RISK & LIQUIDITY' },
  { id: 'Assets', title: 'ASSETS / TRADES' },
];

export const SUBGROUPS: Record<string, string[]> = {
  Events: ['Geo', 'PolicySignals', 'Supply', 'Demand', 'Financial'],
  GlobalMacro: ['US', 'China', 'Commodities', 'GlobalRates', 'GlobalRisk'],
  RussiaMacro: ['Inflation', 'GrowthLabor', 'External', 'Fiscal', 'Banking'],
  Policy: ['CBR', 'Gov', 'Regulation'],
  RatesCurve: ['FrontEnd', 'CurveShape', 'Forwards', 'InflationMarket', 'Credit'],
  RiskLiquidity: ['Vol', 'Flows', 'Liquidity', 'Positioning'],
  Assets: ['FX', 'Bonds', 'Equities', 'Commodities', 'Defensive', 'Cash'],
};

export const ROW_COLORS: Record<string, string> = {
  Events: '#6366f1',
  GlobalMacro: '#3b82f6',
  RussiaMacro: '#8b5cf6',
  Policy: '#a855f7',
  RatesCurve: '#14b8a6',
  RiskLiquidity: '#f43f5e',
  Assets: '#f59e0b',
};

export const NODES: MacroNode[] = [
  { id: 'GEO_SHOCK', row: 'Events', subgroup: 'Geo', icon: '🌍', label: 'Геошок / санкции', desc: 'Санкции, конфликты, эскалация.' },
  { id: 'OPEC_MOVE', row: 'Events', subgroup: 'Supply', icon: '🛢️', label: 'Решение OPEC+', desc: 'Квоты/риторика ОПЕК+.' },
  { id: 'SUPPLY_DISRUPT', row: 'Events', subgroup: 'Supply', icon: '⛔', label: 'Сбой поставок', desc: 'Проблемы добычи/логистики.' },
  { id: 'DEMAND_SHOCK', row: 'Events', subgroup: 'Demand', icon: '📉', label: 'Шок спроса', desc: 'Рецессия/восстановление спроса.' },
  { id: 'CBR_GUIDANCE', row: 'Events', subgroup: 'PolicySignals', icon: '🗣️', label: 'Риторика ЦБ', desc: 'Forward guidance (ястреб/голубь).' },
  { id: 'FISCAL_SHOCK', row: 'Events', subgroup: 'Financial', icon: '🏛️', label: 'Фискальный шок', desc: 'Расходы/налоги/дефицит.' },
  { id: 'CAPITAL_CONTROL', row: 'Events', subgroup: 'Financial', icon: '🔒', label: 'Капконтроли', desc: 'Ограничения движения капитала.' },

  { id: 'US_RATES', row: 'GlobalMacro', subgroup: 'US', icon: '🇺🇸', label: 'Ставки США', desc: 'Ожидания Fed / Treasuries.' },
  { id: 'USD_INDEX', row: 'GlobalMacro', subgroup: 'US', icon: '💵', label: 'DXY доллар', desc: 'Сила доллара глобально.' },
  { id: 'CHINA_GROWTH', row: 'GlobalMacro', subgroup: 'China', icon: '🇨🇳', label: 'Рост Китая', desc: 'Импульс спроса на сырьё.' },
  { id: 'OIL_PRICE', row: 'GlobalMacro', subgroup: 'Commodities', icon: '🛢️', label: 'Цена нефти', desc: 'Brent/Urals уровень и тренд.' },
  { id: 'GLOBAL_RISK', row: 'GlobalMacro', subgroup: 'GlobalRisk', icon: '🧯', label: 'Global risk-off', desc: 'Уход в защиту, рост премии за риск.' },

  { id: 'CPI_TREND', row: 'RussiaMacro', subgroup: 'Inflation', icon: '🧾', label: 'Тренд инфляции', desc: 'Ускорение/замедление CPI (сглажено).' },
  { id: 'INFL_EXP', row: 'RussiaMacro', subgroup: 'Inflation', icon: '🎯', label: 'Ожид. инфляция', desc: 'Ожидания населения/рынка.' },
  { id: 'UNEMP', row: 'RussiaMacro', subgroup: 'GrowthLabor', icon: '👷', label: 'Безработица', desc: 'Рынок труда → зарплаты/инфляция.' },
  { id: 'TRADE_BAL', row: 'RussiaMacro', subgroup: 'External', icon: '⚖️', label: 'Платёжный баланс', desc: 'Текущий счёт, экспорт/импорт.' },
  { id: 'FX_USDRUB', row: 'RussiaMacro', subgroup: 'External', icon: '💱', label: 'USD/RUB', desc: 'Курс рубля и волатильность.' },
  { id: 'BUDGET', row: 'RussiaMacro', subgroup: 'Fiscal', icon: '📒', label: 'Бюджет', desc: 'Доходы/расходы/дефицит.' },

  { id: 'CBR_DEC', row: 'Policy', subgroup: 'CBR', icon: '🏦', label: 'Решение ЦБ', desc: 'Hike/Hold/Cut — ближайшее заседание.' },
  { id: 'EASING_3_6', row: 'Policy', subgroup: 'CBR', icon: '🧭', label: 'P смягч. 3–6м', desc: 'Вероятность смягчения режима.' },

  { id: 'EXP_POLICY_6M', row: 'RatesCurve', subgroup: 'FrontEnd', icon: '⏱️', label: 'Ожид. ставка 6м', desc: 'ExpectedPolicy6m (из кривой).' },
  { id: 'LEVEL', row: 'RatesCurve', subgroup: 'CurveShape', icon: '📍', label: 'Level кривой', desc: 'Уровень кривой (примерно).' },
  { id: 'SLOPE', row: 'RatesCurve', subgroup: 'CurveShape', icon: '📐', label: 'Slope кривой', desc: 'Наклон (10Y-2Y и т.п.).' },
  { id: 'BOND_STRESS', row: 'RatesCurve', subgroup: 'CurveShape', icon: '🚨', label: 'Стресс облигаций', desc: 'Индекс стресса (0–100).' },
  { id: 'CURV', row: 'RatesCurve', subgroup: 'CurveShape', icon: '〰️', label: 'Curvature', desc: 'Кривизна (горб).' },

  { id: 'FX_HEDGE', row: 'Assets', subgroup: 'FX', icon: '🧯', label: 'FX-хедж', desc: 'Хедж валютного риска.' },
  { id: 'OFZ_DUR', row: 'Assets', subgroup: 'Bonds', icon: '🧾', label: 'ОФЗ duration', desc: 'Удлинение/сокращение дюрации.' },
  { id: 'OFZ_CARRY', row: 'Assets', subgroup: 'Bonds', icon: '🪜', label: 'Carry / Roll', desc: 'Carry и роллдаун.' },
  { id: 'EQ_EXPORT', row: 'Assets', subgroup: 'Equities', icon: '🚢', label: 'Экспортёры', desc: 'Нефтегаз/металлы.' },
  { id: 'EQ_DOM', row: 'Assets', subgroup: 'Equities', icon: '🏪', label: 'Внутр. спрос', desc: 'Ритейл/стройка и т.п.' },
  { id: 'GOLD', row: 'Assets', subgroup: 'Defensive', icon: '🥇', label: 'Золото', desc: 'Защитный актив.' },
  { id: 'CASH', row: 'Assets', subgroup: 'Cash', icon: '💵', label: 'Кэш', desc: 'Ликвидность/короткие.' },
];

export const EDGES: MacroEdge[] = [
  { id: 'e_geo_oil', from: 'GEO_SHOCK', to: 'OIL_PRICE', sign: +1, w: 0.65, label: '+ риск-премия', why: 'Геошок повышает риск перебоев/санкций → рынок закладывает премию за риск в нефть.' },
  { id: 'e_geo_risk', from: 'GEO_SHOCK', to: 'GLOBAL_RISK', sign: +1, w: 0.55, label: '+ risk-off', why: 'Рост неопределённости → инвесторы уходят в защиту (risk-off).' },
  { id: 'e_risk_fx', from: 'GLOBAL_RISK', to: 'FX_USDRUB', sign: +1, w: 0.45, label: 'RUB−', why: 'В risk-off EM валюты обычно под давлением → USD/RUB растёт.' },
  { id: 'e_risk_gold', from: 'GLOBAL_RISK', to: 'GOLD', sign: +1, w: 0.40, label: 'gold+', why: 'В risk-off растёт спрос на защитные активы → золото поддержано.' },
  { id: 'e_risk_eqdom', from: 'GLOBAL_RISK', to: 'EQ_DOM', sign: -1, w: 0.55, label: 'risk−', why: 'Risk-off снижает аппетит к риску → внутренние акции под давлением.' },
  { id: 'e_oil_trade', from: 'OIL_PRICE', to: 'TRADE_BAL', sign: +1, w: 0.70, label: '+ экспорт', why: 'Нефть — ключевая статья экспорта РФ → рост нефти улучшает текущий счёт.' },
  { id: 'e_oil_budget', from: 'OIL_PRICE', to: 'BUDGET', sign: +1, w: 0.60, label: '+ доходы', why: 'Рост нефти повышает нефтегазовые доходы бюджета.' },
  { id: 'e_trade_fx', from: 'TRADE_BAL', to: 'FX_USDRUB', sign: -1, w: 0.55, label: 'RUB↑', why: 'Лучший внешний баланс → больше валютной выручки → рубль укрепляется.' },
  { id: 'e_fx_cpi', from: 'FX_USDRUB', to: 'CPI_TREND', sign: +1, w: 0.35, label: '+ импорт', why: 'Ослабление рубля делает импорт дороже → инфляция ускоряется.' },
  { id: 'e_cpi_exp', from: 'CPI_TREND', to: 'INFL_EXP', sign: +1, w: 0.45, label: '+ ожидания', why: 'Если инфляция ускоряется, ожидания инфляции тоже растут.' },
  { id: 'e_exp_cbr', from: 'INFL_EXP', to: 'CBR_DEC', sign: +1, w: 0.55, label: '+ ястреб', why: 'Рост ожиданий инфляции ухудшает якорение → ЦБ склонен к более жёсткому решению.' },
  { id: 'e_cpi_cbr', from: 'CPI_TREND', to: 'CBR_DEC', sign: +1, w: 0.45, label: '+ реакция', why: 'Ускорение инфляции повышает вероятность ужесточения/удержания ставки.' },
  { id: 'e_guid_cbr', from: 'CBR_GUIDANCE', to: 'CBR_DEC', sign: +1, w: 0.35, label: '+ сигнал', why: 'Риторика заранее меняет ожидания рынка по решению.' },
  { id: 'e_cbr_exp6m', from: 'CBR_DEC', to: 'EXP_POLICY_6M', sign: +1, w: 0.85, label: '+ фронт-энд', why: 'Ожидания решения ЦБ напрямую двигают короткие ставки и ExpectedPolicy6m.' },
  { id: 'e_exp6m_level', from: 'EXP_POLICY_6M', to: 'LEVEL', sign: +1, w: 0.60, label: '+ level', why: 'Рост ожиданий короткой ставки частично поднимает весь уровень кривой.' },
  { id: 'e_exp6m_slope', from: 'EXP_POLICY_6M', to: 'SLOPE', sign: -1, w: 0.35, label: 'flatten', why: 'Фронт-энд растёт быстрее длинного конца → кривая уплощается.' },
  { id: 'e_slope_ease', from: 'SLOPE', to: 'EASING_3_6', sign: +1, w: 0.40, label: '+ цикл', why: 'Крутая кривая часто означает ожидание будущего смягчения ставок.' },
  { id: 'e_level_stress', from: 'LEVEL', to: 'BOND_STRESS', sign: +1, w: 0.35, label: '+ стресс', why: 'Очень высокий уровень ставок ухудшает условия финансирования → стресс в облигациях.' },
  { id: 'e_ease_dur', from: 'EASING_3_6', to: 'OFZ_DUR', sign: +1, w: 0.60, label: 'duration+', why: 'Если ожидается смягчение, длинные облигации выигрывают от падения доходностей.' },
  { id: 'e_slope_carry', from: 'SLOPE', to: 'OFZ_CARRY', sign: +1, w: 0.45, label: 'roll+', why: 'Крутая кривая даёт больший роллдаун по мере "скатывания" по кривой.' },
  { id: 'e_stress_cash', from: 'BOND_STRESS', to: 'CASH', sign: +1, w: 0.45, label: 'cash+', why: 'При стрессе ценна ликвидность → кэш/короткие.' },
  { id: 'e_fx_hedge', from: 'FX_USDRUB', to: 'FX_HEDGE', sign: +1, w: 0.55, label: 'hedge+', why: 'Рост риска ослабления/волатильности RUB → потребность в FX-хедже.' },
  { id: 'e_oil_export', from: 'OIL_PRICE', to: 'EQ_EXPORT', sign: +1, w: 0.55, label: 'export+', why: 'Экспортёры выигрывают от роста нефти через выручку и маржу.' },
  { id: 'e_fisc_cpi', from: 'FISCAL_SHOCK', to: 'CPI_TREND', sign: +1, w: 0.40, label: '+ спрос', why: 'Фискальный разгон повышает спрос → усиливает инфляционное давление.' },
];

export const SCENARIOS: MacroScenario[] = [
  {
    id: 'S1', tag: 'geo', name: 'Геошок → нефть + risk-off', shock: 'GEO_SHOCK +1σ',
    summary: 'Проход в нефть, risk-off, рубль, инфляцию и предпочтения активов.',
    paths: [
      ['GEO_SHOCK', 'OIL_PRICE', 'TRADE_BAL', 'FX_USDRUB', 'CPI_TREND', 'INFL_EXP', 'CBR_DEC', 'EXP_POLICY_6M', 'LEVEL', 'BOND_STRESS', 'CASH'],
      ['GEO_SHOCK', 'GLOBAL_RISK', 'FX_USDRUB', 'FX_HEDGE'],
      ['GEO_SHOCK', 'GLOBAL_RISK', 'GOLD'],
      ['GEO_SHOCK', 'GLOBAL_RISK', 'EQ_DOM'],
    ],
  },
  {
    id: 'S2', tag: 'oil', name: 'Нефть +20%', shock: 'OIL_PRICE +20%',
    summary: 'Нефть вверх → баланс/рубль → инфляция/ЦБ → кривая → ОФЗ и экспортёры.',
    paths: [
      ['OIL_PRICE', 'TRADE_BAL', 'FX_USDRUB', 'CPI_TREND', 'INFL_EXP', 'CBR_DEC', 'EXP_POLICY_6M', 'SLOPE', 'EASING_3_6', 'OFZ_DUR'],
      ['OIL_PRICE', 'EQ_EXPORT'],
      ['OIL_PRICE', 'BUDGET'],
    ],
  },
  {
    id: 'S3', tag: 'oil', name: 'Нефть −20%', shock: 'OIL_PRICE −20%',
    summary: 'Нефть вниз → давление на рубль → инфляция через FX → стресс/кэш.',
    paths: [
      ['OIL_PRICE', 'TRADE_BAL', 'FX_USDRUB', 'CPI_TREND', 'INFL_EXP', 'CBR_DEC', 'EXP_POLICY_6M', 'LEVEL', 'BOND_STRESS', 'CASH'],
      ['OIL_PRICE', 'EQ_EXPORT'],
    ],
  },
  {
    id: 'S4', tag: 'cbr', name: 'ЦБ ястребит (guidance)', shock: 'CBR_GUIDANCE +1',
    summary: 'Жёсткая риторика → ожидания ставки ↑ → стресс/кэш.',
    paths: [['CBR_GUIDANCE', 'CBR_DEC', 'EXP_POLICY_6M', 'LEVEL', 'BOND_STRESS', 'CASH']],
  },
  {
    id: 'S5', tag: 'cbr', name: 'ЦБ голубит (guidance)', shock: 'CBR_GUIDANCE −1',
    summary: 'Мягкая риторика → рост P(easing) → выигрыш дюрации.',
    paths: [['CBR_GUIDANCE', 'CBR_DEC', 'EXP_POLICY_6M', 'SLOPE', 'EASING_3_6', 'OFZ_DUR']],
  },
  {
    id: 'S6', tag: 'infl', name: 'Инфляция замедляется', shock: 'CPI_TREND ↓↓',
    summary: 'Инфляция ↓ → ожидания ↓ → ЦБ мягче → easing → дюрация.',
    paths: [['CPI_TREND', 'INFL_EXP', 'CBR_DEC', 'EXP_POLICY_6M', 'SLOPE', 'EASING_3_6', 'OFZ_DUR']],
  },
  {
    id: 'S7', tag: 'infl', name: 'Инфляция ускоряется', shock: 'CPI_TREND ↑',
    summary: 'Инфляция ↑ → ЦБ жёстче → стресс → кэш.',
    paths: [['CPI_TREND', 'INFL_EXP', 'CBR_DEC', 'EXP_POLICY_6M', 'LEVEL', 'BOND_STRESS', 'CASH']],
  },
  {
    id: 'S8', tag: 'risk', name: 'Global risk-off', shock: 'GLOBAL_RISK +1σ',
    summary: 'Risk-off → давление на RUB и риск-активы → золото/хедж.',
    paths: [
      ['GLOBAL_RISK', 'FX_USDRUB', 'FX_HEDGE'],
      ['GLOBAL_RISK', 'GOLD'],
      ['GLOBAL_RISK', 'EQ_DOM'],
    ],
  },
  {
    id: 'S9', tag: 'geo', name: 'Фискальный разгон', shock: 'FISCAL_SHOCK +1σ',
    summary: 'Расходы ↑ → инфляция ↑ → ЦБ → стресс → кэш.',
    paths: [['FISCAL_SHOCK', 'CPI_TREND', 'INFL_EXP', 'CBR_DEC', 'EXP_POLICY_6M', 'LEVEL', 'BOND_STRESS', 'CASH']],
  },
  {
    id: 'S10', tag: 'geo', name: 'Капконтроли (демо)', shock: 'CAPITAL_CONTROL +1',
    summary: 'Демо: подсветка узла. Связи можно добавить по мере расширения модели.',
    paths: [['CAPITAL_CONTROL']],
  },
];

// Helper: find edge by from→to
export function findEdge(from: string, to: string): MacroEdge | undefined {
  return EDGES.find((e) => e.from === from && e.to === to);
}

// Helper: convert a path of node IDs to edge IDs
export function pathToEdgeIds(path: string[]): string[] {
  const ids: string[] = [];
  for (let i = 0; i < path.length - 1; i++) {
    const edge = findEdge(path[i], path[i + 1]);
    if (edge) ids.push(edge.id);
  }
  return ids;
}

// Lookup maps
export const nodeById = new Map(NODES.map((n) => [n.id, n]));
export const edgeById = new Map(EDGES.map((e) => [e.id, e]));
